const STORAGE_KEY = 'tf:sherpaConfig';

export const AUTH_SCHEMES = Object.freeze({
  BEARER: 'bearer',
  TOKEN: 'token',
  RAW: 'raw',
  NONE: 'none',
});

let override = {};

export function setRuntimeOverride(partial) {
  override = { ...override, ...partial };
}

export function clearRuntimeOverride() {
  override = {};
}

function readEnv() {
  // `import.meta.env` only exists when bundled by Vite (standalone mode).
  // Webpack (SherpaCRM embed) does not populate it; guard so the file is safe to import in both.
  const env = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env : {};
  return {
    baseUrl: env.VITE_SHERPA_API_BASE_URL || '',
    authScheme: env.VITE_SHERPA_AUTH_SCHEME || AUTH_SCHEMES.BEARER,
    token: env.VITE_SHERPA_TOKEN || '',
    version: env.VITE_SHERPA_VERSION || '4.0.0',
    testPath: env.VITE_SHERPA_TEST_PATH || '',
    lang: env.VITE_SHERPA_LANG || 'ca_es',
    profile: env.VITE_SHERPA_PROFILE || '',
  };
}

function readStorage() {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

export function getRuntimeConfig() {
  const env = readEnv();
  const stored = readStorage();
  const pick = (key, fallback) => override[key] ?? stored[key] ?? env[key] ?? fallback;
  return {
    baseUrl: pick('baseUrl', ''),
    authScheme: pick('authScheme', AUTH_SCHEMES.BEARER) || AUTH_SCHEMES.BEARER,
    token: pick('token', ''),
    version: pick('version', '4.0.0') || '4.0.0',
    testPath: pick('testPath', ''),
    lang: pick('lang', 'ca_es') || 'ca_es',
    profile: pick('profile', ''),
    fromOverride: {
      baseUrl: override.baseUrl != null,
      token: override.token != null,
      authScheme: override.authScheme != null,
      version: override.version != null,
      testPath: override.testPath != null,
    },
    fromStorage: {
      baseUrl: stored.baseUrl != null,
      token: stored.token != null,
      authScheme: stored.authScheme != null,
      version: stored.version != null,
      testPath: stored.testPath != null,
    },
  };
}

export function isAbsoluteUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

export function effectiveBaseUrl(config = getRuntimeConfig()) {
  if (isAbsoluteUrl(config.baseUrl)) return config.baseUrl;
  return '/sherpa-api';
}

export function saveRuntimeConfig(partial) {
  if (typeof localStorage === 'undefined') return;
  const current = readStorage();
  const next = { ...current, ...partial };
  for (const k of Object.keys(next)) {
    if (next[k] === '' || next[k] == null) delete next[k];
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function clearRuntimeConfig() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function applyAuthHeaders(headers, config = getRuntimeConfig()) {
  if (!config.token) return headers;
  switch (config.authScheme) {
    case AUTH_SCHEMES.BEARER:
      headers.Authorization = `bearer ${config.token}`;
      break;
    case AUTH_SCHEMES.TOKEN:
      headers.token = config.token;
      break;
    case AUTH_SCHEMES.RAW:
      headers.Authorization = config.token;
      break;
    case AUTH_SCHEMES.NONE:
    default:
      break;
  }
  return headers;
}
